/**
 * Cria a sessão do funil de cadastro na entrada — Home / hubs / deep link em
 * rota do funil quando ainda não há patientId no LS.
 *
 * A criação do patientId ficou no CTA (Home/hubs) quando o A/B de ordem
 * existia. O experimento está suspenso: a sessão sempre nasce no controle
 * (nome primeiro, `/consulta`). O NameForm continua podendo criar se o
 * paciente abrir `/consulta` direto.
 */

import {
  CONSULTATION_EPHEMERAL_KEYS,
  clearConsultationEphemeralKeys,
} from './consultationSessionBound';
import { cancelScheduledBackup, clearSessionBackup } from './sessionBackup';
import { persistIdentityKey } from './identityBackup';
import { SIGNUP_TYPED_MARK_KEYS } from './signupFieldPrefill';
import {
  setSignupVariant,
  firstSignupRoute,
  SIGNUP_DEFAULT_VARIANT,
} from './signupOrderAb';
import { clearFlowVariant } from './flowVariant';
import { assignSignupUxVariant, clearSignupUxVariant } from './signupUxAb';

const AUTO_PATIENT_ID_RE = /^patient-\d{6,}(?:-[a-z0-9]+)*$/i;

function readPatientId() {
  try {
    return (
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      ''
    );
  } catch {
    return '';
  }
}

function isLikelyAutoPatientId(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  return AUTO_PATIENT_ID_RE.test(raw);
}

function newPatientId() {
  return `patient-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function persistPatientId(patientId) {
  try {
    sessionStorage.setItem('patientId', patientId);
    localStorage.setItem('patientId', patientId);
  } catch {}
  try { persistIdentityKey('patientId', patientId); } catch {}
}

/**
 * Cadastro que o próprio paciente digita no funil. Separado de
 * `FUNNEL_FIELD_KEYS` para poder sobreviver ao ramo de criação — ver
 * `snapshotSession`.
 */
const SIGNUP_IDENTITY_KEYS = [
  'fullName',
  'patientName',
  'patientEmail',
  'patientCpf',
  'patientPhone',
  'patientEmailDraft',
];

const FUNNEL_FIELD_KEYS = [
  ...SIGNUP_IDENTITY_KEYS,
  ...SIGNUP_TYPED_MARK_KEYS,
  'paymentConfirmed',
  'paymentConfirmedFor',
  'paymentConfirmedAt',
  'payerName',
  'conversionTracked',
  'instantCouponAb',
  ...CONSULTATION_EPHEMERAL_KEYS,
];

function clearStorageKeys(keys) {
  keys.forEach((key) => {
    try { localStorage.removeItem(key); } catch {}
    try { sessionStorage.removeItem(key); } catch {}
  });
}

/**
 * Cadastro digitado NESTA aba, para atravessar o ramo de criação.
 *
 * Cada passo do funil grava o campo e só então chama `ensureSignupSession`,
 * porque precisa do patientId para o `/registration/step`. Sem patientId
 * automático (deep link em `/telefone`, storage limpo, id de consulta já paga),
 * a limpeza do ramo de criação apagava justamente o que o paciente acabara de
 * digitar — o campo voltava vazio no passo seguinte.
 *
 * Lê só `sessionStorage`, pela mesma razão de `signupFieldPrefill`: em aparelho
 * compartilhado o `localStorage` pode guardar o cadastro de quem usou antes, e
 * a aba atual não deve herdar isso. `paymentConfirmed` e as chaves da consulta
 * ficam de fora de propósito — sessão nova não nasce paga nem com triagem.
 */
function snapshotSession(keys) {
  const snapshot = {};
  keys.forEach((key) => {
    try {
      const value = sessionStorage.getItem(key);
      if (value) snapshot[key] = value;
    } catch {}
  });
  return snapshot;
}

function restoreIdentity(snapshot) {
  Object.keys(snapshot).forEach((key) => {
    // Grava LS + SS e, nas chaves de identidade, também cookie e IDB.
    try { persistIdentityKey(key, snapshot[key]); } catch {}
  });
}

/** Marcas de digitação voltam só para o `sessionStorage` — são por aba. */
function restoreTypedMarks(snapshot) {
  Object.keys(snapshot).forEach((key) => {
    try { sessionStorage.setItem(key, snapshot[key]); } catch {}
  });
}

/**
 * Sorteia o braço do signup-ux-ab ANTES do primeiro clique na Home.
 *
 * Sem isto o CTA da Home sempre saía "Iniciar consulta agora": o patientId
 * só nascia no clique, e `usesGuidedCopy()` caía no controle. Não limpa
 * sessão existente — só cria patientId se ainda não houver um automático.
 *
 * @returns {string} patientId vigente (ou vazio se storage falhar / id pago)
 */
export function primeSignupUxSession() {
  const existing = readPatientId();
  if (existing && isLikelyAutoPatientId(existing)) {
    setSignupVariant(SIGNUP_DEFAULT_VARIANT, existing);
    assignSignupUxVariant(existing);
    return existing;
  }
  if (existing) return existing;
  const patientId = newPatientId();
  persistPatientId(patientId);
  setSignupVariant(SIGNUP_DEFAULT_VARIANT, patientId);
  assignSignupUxVariant(patientId);
  return patientId;
}

/**
 * Garante sessão de cadastro (patientId + chaves limpas). Sempre no
 * controle — o A/B de ordem não deve sortear.
 *
 * @returns {Promise<{patientId: string, variant: string, firstRoute: string, created: boolean}>}
 */
export async function ensureSignupSession(opts = {}) {
  const existing = readPatientId();
  const forceNew = opts && opts.forceNew === true;
  const resetFields = opts && opts.resetFields === true;
  if (!forceNew && existing && isLikelyAutoPatientId(existing)) {
    setSignupVariant(SIGNUP_DEFAULT_VARIANT, existing);
    // Idempotente: o braço vem de hash do patientId, então reentrar no funil
    // com a mesma sessão não remaneja ninguém.
    assignSignupUxVariant(existing);
    // CTA da Home/hubs: nova consulta no mesmo patientId (A/B estável),
    // sem reaproveitar nome/e-mail de quem usou o aparelho antes.
    if (resetFields) clearStorageKeys(FUNNEL_FIELD_KEYS);
    return {
      patientId: existing,
      variant: SIGNUP_DEFAULT_VARIANT,
      firstRoute: firstSignupRoute(SIGNUP_DEFAULT_VARIANT),
      created: false,
    };
  }

  // `resetFields` é o pedido explícito de começar do zero (CTA da Home/hubs):
  // aí a limpeza do cadastro é o objetivo. Nos demais casos quem chama está no
  // meio do funil e só quer garantir que existe patientId.
  const keptIdentity = resetFields ? null : snapshotSession(SIGNUP_IDENTITY_KEYS);
  const keptMarks = resetFields ? null : snapshotSession(SIGNUP_TYPED_MARK_KEYS);

  try { cancelScheduledBackup(); } catch {}
  clearConsultationEphemeralKeys();
  clearFlowVariant();
  clearSignupUxVariant();

  clearStorageKeys(['patientId', ...FUNNEL_FIELD_KEYS]);

  try { await clearSessionBackup(); } catch {}

  const patientId = newPatientId();
  persistPatientId(patientId);
  if (keptIdentity) restoreIdentity(keptIdentity);
  if (keptMarks) restoreTypedMarks(keptMarks);
  setSignupVariant(SIGNUP_DEFAULT_VARIANT, patientId);
  assignSignupUxVariant(patientId);

  return {
    patientId,
    variant: SIGNUP_DEFAULT_VARIANT,
    firstRoute: firstSignupRoute(SIGNUP_DEFAULT_VARIANT),
    created: true,
  };
}

export const SIGNUP_ORDER_CONTROL = SIGNUP_DEFAULT_VARIANT;

